import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, FileImage, FileVideo, AlertCircle, Camera, Video, StopCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const MediaCapture = ({
  onFilesChange,
  acceptedTypes = ['image/*', 'video/*'],
  maxFileSize = 50 * 1024 * 1024, // 50MB default
  maxVideoLength = 30, // 30 seconds for video
  multiple = true,
  className = '',
  label = 'Upload or Capture Media'
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState('photo'); // 'photo' or 'video'
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [stream, setStream] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const chunksRef = useRef([]);

  // Cleanup camera stream when component unmounts or camera closes
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Set video stream when it becomes available
  useEffect(() => {
    if (stream && videoRef.current && showCamera) {
      videoRef.current.srcObject = stream;

      // Play the video to ensure it starts
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
      });
    }
  }, [stream, showCamera]);

  // Update recording timer
  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= maxVideoLength) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      setRecordingTime(0);
    }

    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [isRecording, maxVideoLength]);

  const validateFile = (file) => {
    const errors = [];

    // Check file size
    if (file.size > maxFileSize) {
      errors.push(`File size exceeds ${Math.round(maxFileSize / (1024 * 1024))}MB limit`);
    }

    // Check file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heif'];
    const allowedVideoTypes = ['video/mp4', 'video/mov', 'video/webm'];

    if (isImage && !allowedImageTypes.includes(file.type)) {
      errors.push('Supported image formats: JPG, PNG, WEBP, HEIF');
    }

    if (isVideo && !allowedVideoTypes.includes(file.type)) {
      errors.push('Supported video formats: MP4, MOV, WEBM');
    }

    if (!isImage && !isVideo) {
      errors.push('Only images and videos are allowed');
    }

    return errors;
  };

  const validateVideoLength = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('video/')) {
        resolve([]);
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;

        if (duration > maxVideoLength) {
          resolve([`Video must be ${maxVideoLength} seconds or shorter`]);
        } else {
          resolve([]);
        }
      };

      video.onerror = () => {
        resolve(['Unable to validate video duration']);
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const createPreview = (file) => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => resolve({ type: 'image', url: e.target.result });
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        const url = URL.createObjectURL(file);
        resolve({ type: 'video', url });
      } else {
        resolve(null);
      }
    });
  };

  const handleFiles = async (fileList) => {
    const newFiles = Array.from(fileList);
    const validFiles = [];
    const newPreviews = [];

    for (const file of newFiles) {
      const validationErrors = validateFile(file);

      if (validationErrors.length > 0) {
        toast.error(`${file.name}: ${validationErrors.join(', ')}`);
        continue;
      }

      const videoErrors = await validateVideoLength(file);
      if (videoErrors.length > 0) {
        toast.error(`${file.name}: ${videoErrors.join(', ')}`);
        continue;
      }

      validFiles.push(file);
      const preview = await createPreview(file);
      if (preview) {
        newPreviews.push({
          id: Date.now() + Math.random(),
          file,
          preview,
          name: file.name,
          size: file.size,
          type: file.type.startsWith('image/') ? 'image' : 'video'
        });
      }
    }

    const updatedFiles = multiple ? [...files, ...validFiles] : validFiles;
    const updatedPreviews = multiple ? [...previews, ...newPreviews] : newPreviews;

    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    onFilesChange(updatedFiles);

    if (validFiles.length > 0) {
      toast.success(`${validFiles.length} file(s) added successfully`);
    }
  };

  const removeFile = (index) => {
    const fileToRemove = previews[index];
    if (fileToRemove.preview.type === 'video') {
      URL.revokeObjectURL(fileToRemove.preview.url);
    }

    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);

    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    onFilesChange(updatedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e) => {
    handleFiles(e.target.files);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Check if device supports camera
  const checkCameraSupport = () => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  };

  // Detect if user is on mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
  };

  // Camera functions
  const startCamera = async (mode) => {
    // Check camera support first
    if (!checkCameraSupport()) {
      toast.error('Camera not supported on this device or browser');
      return;
    }

    try {
      setCameraMode(mode);
      setCameraReady(false);

      // Different constraints for mobile vs desktop
      const isMobile = isMobileDevice();
      const constraints = {
        video: isMobile ? {
          facingMode: { ideal: 'environment' }, // Prefer back camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } : {
          // Desktop: just request video without facingMode
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: mode === 'video' // Only enable audio for video recording
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      setShowCamera(true);

      // Wait for video element to be ready and set stream
      // This will be handled in the useEffect below
    } catch (error) {
      console.error('Error accessing camera:', error);

      // Provide more specific error messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No camera found on this device.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Camera is already in use by another application.');
      } else {
        toast.error('Unable to access camera. Please check permissions and try again.');
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    setCameraReady(false);
    setIsRecording(false);
    setRecordingTime(0);
    chunksRef.current = [];
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !stream) {
      toast.error('Camera not ready. Please wait for camera to initialize.');
      return;
    }

    if (!cameraReady) {
      toast.error('Camera is still loading. Please wait a moment.');
      return;
    }

    try {
      const video = videoRef.current;

      // Additional check for video dimensions
      if (!video.videoWidth || !video.videoHeight) {
        toast.error('Camera is still initializing. Please wait a moment.');
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error('Failed to capture photo. Please try again.');
          return;
        }

        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        await handleFiles([file]);
        stopCamera();
        toast.success('Photo captured successfully!');
      }, 'image/jpeg', 0.95);
    } catch (error) {
      console.error('Photo capture error:', error);
      toast.error('Failed to capture photo. Please try again.');
    }
  };

  const startRecording = () => {
    if (!stream) return;

    try {
      chunksRef.current = [];
      const options = {
        mimeType: 'video/webm;codecs=vp8,opus',
        videoBitsPerSecond: 2500000
      };

      mediaRecorderRef.current = new MediaRecorder(stream, options);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });

        await handleFiles([file]);
        stopCamera();
        toast.success('Video recorded successfully!');
      };

      mediaRecorderRef.current.start(100);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      {/* Camera View */}
      {showCamera ? (
        <div className="relative bg-black rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={cameraMode !== 'video'}
            className="w-full h-auto"
            onLoadedMetadata={() => {
              console.log('Video metadata loaded');
            }}
            onCanPlay={() => {
              console.log('Video can play');
              setCameraReady(true);
            }}
            onPlaying={() => {
              console.log('Video is playing');
              setCameraReady(true);
            }}
          />

          {/* Recording indicator */}
          {isRecording && (
            <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-2 rounded-full flex items-center space-x-2 animate-pulse">
              <div className="w-3 h-3 bg-white rounded-full"></div>
              <span className="font-medium">{formatRecordingTime(recordingTime)} / {maxVideoLength}s</span>
            </div>
          )}

          {/* Loading indicator */}
          {!cameraReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="text-center text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-lg font-medium">Initializing camera...</p>
              </div>
            </div>
          )}

          {/* Camera Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <div className="flex justify-center items-center space-x-4">
              <button
                onClick={stopCamera}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={capturePhoto}
                disabled={!cameraReady}
                className="bg-white hover:bg-gray-100 text-gray-900 p-4 rounded-full transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Camera className="h-8 w-8" />
              </button>
            </div>

            <p className="text-white text-center text-sm mt-4">
              {cameraReady ? 'Tap the button to capture photo' : 'Please wait, camera is loading...'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Upload Area */}
          <div
            className={`
              border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
              ${dragOver
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 hover:border-gray-400'
              }
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">
              Drop files here or click to browse
            </p>
            <p className="text-xs text-gray-500">
              Supported: JPG, PNG, WEBP, HEIF, MP4, MOV, WEBM
            </p>
            <p className="text-xs text-gray-500">
              Max video length: {maxVideoLength}s, Max file size: {Math.round(maxFileSize / (1024 * 1024))}MB
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple={multiple}
            accept={acceptedTypes.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Camera Capture Button - Photo Only */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => startCamera('photo')}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Camera className="h-5 w-5" />
              <span>Capture Photo</span>
            </button>
          </div>
        </>
      )}

      {/* File Previews */}
      {!showCamera && previews.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {previews.map((item, index) => (
            <div key={item.id} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border">
                {item.preview.type === 'image' ? (
                  <img
                    src={item.preview.url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <video
                    src={item.preview.url}
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                  />
                )}

                {/* Remove button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <X className="h-4 w-4" />
                </button>

                {/* File type indicator */}
                <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                  {item.type === 'image' ? (
                    <FileImage className="h-4 w-4" />
                  ) : (
                    <FileVideo className="h-4 w-4" />
                  )}
                </div>
              </div>

              {/* File info */}
              <div className="mt-2 text-xs text-gray-600">
                <p className="truncate" title={item.name}>{item.name}</p>
                <p>{formatFileSize(item.size)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Guidelines - Commented out to reduce vertical space */}
      {/* {!showCamera && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Media Guidelines:</p>
              <ul className="space-y-1 text-xs">
                <li>• Upload from device or capture using camera</li>
                <li>• Images: JPG, PNG, WEBP, HEIF formats supported</li>
                <li>• Videos: MP4, MOV, WEBM formats, max {maxVideoLength} seconds</li>
                <li>• Maximum file size: {Math.round(maxFileSize / (1024 * 1024))}MB per file</li>
                <li>• {multiple ? 'Multiple files allowed' : 'Single file only'}</li>
              </ul>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default MediaCapture;
